import React, { useState, useContext } from "react";
import { Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { getToken } from "../../../../api/Account";
import { tienePermiso } from "../../../../helpers/permisos";
import { DataContext } from "../../../../context/DataContext";
import { EstadoChip, useAviso } from "../../../../components/base";
import { deleteReposoAsync, getRepososAsync } from "../../../../api/Nomina";
import AddReposo from "../reposos/AddReposo";
import HistorialTab from "./HistorialTab";
import {
  celdaFecha,
  periodo,
  useConfirmacion,
  useEscritura,
  useListaRemota,
} from "../comunes";

// `isCerrado`: la nómina donde se aplicó ya se cerró.
const EstadoNomina = ({ cerrado }) =>
  cerrado ? (
    <EstadoChip estado="neutro" label="Cerrado" />
  ) : (
    <EstadoChip estado="info" label="Abierto" />
  );

const Reposos = ({ id }) => {
  const [showModal, setShowModal] = useState(false);

  const token = getToken();
  const { access } = useContext(DataContext);
  const aviso = useAviso();
  const escribir = useEscritura();
  const { pedir, dialogo } = useConfirmacion();

  const {
    datos: historyReposo,
    cargando,
    error,
    reload: localReload,
    setReload: setLocalReload,
  } = useListaRemota(() => getRepososAsync(token, id));

  const deleteReposo = async (reposoId) => {
    const ok = await escribir(
      () => deleteReposoAsync(token, reposoId),
      "No se pudo eliminar el reposo"
    );
    if (!ok) return;
    setLocalReload(!localReload);
    aviso.exito("Reposo eliminado");
  };

  const pedirEliminar = (row) =>
    pedir({
      titulo: "Eliminar reposo",
      mensaje: (
        <>
          <Typography component="p" sx={{ mb: 1 }}>
            {`Se eliminará el reposo #${row.id}${row.motivo ? ` «${row.motivo}»` : ""} (${periodo(
              row.desde,
              row.hasta
            )}, ${row.dias} ${Number(row.dias) === 1 ? "día" : "días"}).`}
          </Typography>
          <Typography component="p" sx={{ color: "text.secondary" }}>
            Esta acción no se puede deshacer.
          </Typography>
        </>
      ),
      textoConfirmar: "Eliminar reposo",
      peligro: true,
      onConfirmar: () => deleteReposo(row.id),
    });

  // Agregar es el Add... de EmployeesController/NominasController (NOMINA CREAR).
  const puedeAgregar = tienePermiso(access, "NOMINA CREAR");
  const puedeEliminar = tienePermiso(access, "NOMINA DELETE");

  const columnas = [
    {
      id: "id",
      etiqueta: "#",
      desde: "md",
      sx: { fontVariantNumeric: "tabular-nums" },
      render: (row) => row.id,
    },
    {
      id: "motivo",
      etiqueta: "Motivo",
      render: (row) => (
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {row.motivo}
        </Typography>
      ),
    },
    {
      id: "periodo",
      etiqueta: "Período",
      desde: "sm",
      sx: celdaFecha,
      render: (row) => periodo(row.desde, row.hasta),
    },
    {
      id: "dias",
      etiqueta: "Días",
      alinear: "right",
      sx: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
      render: (row) => row.dias,
    },
    {
      id: "estado",
      etiqueta: "Nómina",
      desde: "sm",
      render: (row) => <EstadoNomina cerrado={row.isCerrado} />,
    },
  ];

  return (
    <div>
      <HistorialTab
        titulo="Reposos"
        descripcion="Reposos de hasta 3 días. Desde 4 días se registra un subsidio."
        etiquetaTabla="Historial de reposos"
        agregar={
          puedeAgregar ? { etiqueta: "Agregar reposo", onClick: () => setShowModal(true) } : undefined
        }
        cargando={cargando}
        error={error}
        onReintentar={() => setLocalReload(!localReload)}
        etiquetaCarga="Cargando reposos…"
        filas={historyReposo}
        columnas={columnas}
        detalleMovil={(row) => (
          <>
            <Typography variant="caption" component="span" sx={{ display: "block", mb: 0.5 }}>
              {periodo(row.desde, row.hasta)}
            </Typography>
            <EstadoNomina cerrado={row.isCerrado} />
          </>
        )}
        etiquetaFila={(row) => `reposo #${row.id}`}
        vacio={{
          titulo: "Sin reposos",
          descripcion: "Aquí aparecen los reposos de hasta 3 días del empleado.",
        }}
        acciones={(row) => [
          {
            id: "eliminar",
            etiqueta: "Eliminar",
            icono: <DeleteOutlineIcon />,
            peligro: true,
            visible: !row.isCerrado && puedeEliminar,
            onClick: () => pedirEliminar(row),
          },
        ]}
      />

      {showModal ? (
        <AddReposo
          id={id}
          setShowModal={setShowModal}
          reload={localReload}
          setReload={setLocalReload}
        />
      ) : null}

      {dialogo}
    </div>
  );
};

export default Reposos;
